import { Component, OnInit } from '@angular/core';
import { UserService, ModuleService, UserProfileService, RoleService } from '../../../mesc.service';
import { User } from '../../../../../shared/model';
import { BaseGridTemplate } from '../../../../../shared/cgp-grid/base-cgp-grid.template';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { AuthenticationService, Module } from '../../../../../auth.service';
import { baseConfig } from 'src/config/config.base';

@Component({
    selector: 'mesc-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss']
})
export class UsersComponent extends BaseGridTemplate implements OnInit {

    userSearch: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    searchForm: FormGroup = this.formBuilder.group({ value: '' });
    clearEnabled: boolean;
    modules: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    unfilteredRoles: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    roles: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    isMescAdmin = false;

    constructor(
        private userService: UserService,
        private moduleService: ModuleService,
        private roleService: RoleService,
        private formBuilder: FormBuilder,
        private userProfileService: UserProfileService,
        private authService: AuthenticationService,
    ) {
        super();
    }

    ngOnInit() {
        this.userService.subscribe(x => {
            let users: any[] = x;
            if (users[0] && users[0].GMIN) {
                if (users[0].GMIN <= 0) users.shift();
            }
            this.userSearch.next(users);
        });

        this.searchForm.valueChanges
            .subscribe(x => this.search(x.value));

        this.moduleService
            .subscribe(data => {
                let filter = data;
                filter = this.filterModulesByGroup(data);
                this.modules.next(filter);
            })

        this.roleService
            .subscribe(data => {
                this.unfilteredRoles.next(data);
                this.roles.next(data);
            })

        this.userService.read('', true);
        this.moduleService.read('', true);
        this.roleService.read('', true);
    }

    handleRead = () => {
        return this.userService.read('')
            .then(() => this.userSearch.value);
    }


    search = (value: string) => {
        if (value.length > 1) {
            this.clearEnabled = true;
            let check = value.split(' ');
            this.userSearch.next(
                this.userService.value.filter(x => {
                    let fields: string[] = [x.FirstName, x.LastName, x.GMIN];
                    for (let c of check) {
                        for (let f of fields) {
                            if (f && f.toString().toLowerCase().indexOf(c.toLowerCase()) !== -1) return true;
                        }
                    }
                    return false;
                })
            );
        } else {
            this.clearEnabled = false;
            this.userSearch.next(this.userService.value);
        }
    }

    clear = () => {
        this.searchForm.reset({ value: '' });
    }


    handleSave = (dataItem, isNew) => {
        return this.userService.save(dataItem, isNew, "", false, false)
            .then(() => {
                this.userProfileService.read('', true);
            })
    }

    handleEdit = (dataItem) => {
        this.editDataItem = dataItem;
        // this.handleModuleChange(dataItem.ModuleId);
    }


    setModelHandler(): void {
        this.editDataItem = new User();
        this.editDataItem.IsActive = true;
    }


    /**
     * Filter out modules based on admin privileges
     * MESC ADMIN: Full access - all modules
     * Module Admin: Restricted Access - single module
     * NOTE: Admins may have multiple groups
     */
    filterModulesByGroup(data: any[]): any[] {
        let groups = this.authService.getAdminUserGroups();
        if (groups && groups.some(groupName => groupName === Module.MESC)) {
            this.isMescAdmin = true;
            return data;
        }
        return data.filter(obj => groups.some(groupName => {
            return groupName.toLowerCase() === obj.Name.toLowerCase();
        }));
    }

    showControl = (name: string): boolean => {
        let show: boolean = true;
        switch (name.toLowerCase()) {
            case 'delete':
                show = this.isMescAdmin;
                if (show) this.editForm.get('IsActive').enable();
                break;
            default:
                break;
        }
        return show;
    }

    handleModuleChange = (data: any) => {

        const selectedModuleIds = data;
        let roles = this.unfilteredRoles.value;
        let selectedModules = this.modules.value.filter(x => selectedModuleIds.some(y => y == x.Id));

        let moduleRoles = baseConfig.modules.Modules;


        let filteredRoles = roles.filter(x => selectedModules.some(y => (x.RoleName.indexOf(y.Name) > -1)));
        this.roles.next(filteredRoles);
    }

    editForm = this.formBuilder.group({
        Id: [],
        CreatedDate: [],
        CreatedBy: [],
        GMIN: ["", Validators.required],
        FirstName: [],
        LastName: [],
        Email: [],
        ModuleId: [{ value: [] }, Validators.required],
        RoleId: [{ value: [] }],
        IsActive: [{ value: true, disabled: !this.isMescAdmin }],
    })

    columns = [
        {
            field: "FirstName",
            title: "First Name",
        },
        {
            field: "LastName",
            title: "Last Name"
        },
        {
            field: "Email",
            title: "Email",
            template: {
                type: 'email',
            }
        },
        {
            field: "ModuleNames",
            title: "Module"
        },
        {
            field: "RoleNames",
            title: "Role"
        },
        {
            field: "LastLogonDate",
            title: "Last Login Date",
            template: {
                type: 'date',
                dateFormat: 'mediumDate'
            },
            width: 150
        },
    ]

    template = {
        controls: {
            add: true,
            edit: true,
            delete: true,
            excel: true,
        },
        showControl: this.showControl,
        columns: this.columns,
        save: this.handleSave,
        read: this.handleRead,
        edit: this.handleEdit,
        service: this.userService,
        fileName: "mesc_users.xlsx"
    }

    formDialog = {
        title: "User",
        formControls: [
            {
                type: "person",
                name: "GMIN",
                title: "GMIN/GMID",
                service: this.userProfileService,
            },
            {
                type: "multiselect",
                name: "ModuleId",
                title: "Modules",
                textField: 'Name',
                valueField: 'Id',
                placeholder: 'Select modules...',
                service: this.modules,
                // onChange: this.handleModuleChange,
            },
            {
                type: "multiselect",
                name: "RoleId",
                title: "Roles",
                textField: 'RoleName',
                valueField: 'Id',
                placeholder: 'Select roles...',
                service: this.roles,
            },
        ],
    }


}
